import React, { useState } from "react"
import styled from "styled-components"
import { useQuery } from "@apollo/client"
import { Button, Theme } from "@pga/pga-component-library"
import { COACH_TOOLS_API } from "../../apolloClient"
import { MEMBERSHIP_APPLICATION_DENIAL_QUERY } from "../queries/membershipApplicationDenial"
import {
  applicationTypeLabel,
  deniedReasonLabel,
  isDeniedPtmApplication,
} from "../../utils/prospect/ptmApplicationStatus"
import {
  ModalTitle,
  StyledModal,
  SubText,
} from "../widgets/Prospect/PathBox/styles"

const MEMBERSHIP_APPS_EMAIL = "membershipapps@pgahq.com"
const MEMBERSHIP_SERVICES_PHONE = "(800) 474-2776"

const ViewMessageButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: ${Theme.fonts.Montserrat};
  font-size: 13px;
  font-weight: 600;
  color: ${Theme.colors.lightBlue};
  text-decoration: underline;
  cursor: pointer;
`

const DetailList = styled.dl`
  margin-bottom: 1rem;

  dt {
    font-family: ${Theme.fonts.Montserrat};
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    color: ${Theme.colors.darkGrey};
  }

  dd {
    margin-bottom: 0.5rem;
    color: ${Theme.colors.darkGrey};
    font-size: 0.9rem;
  }
`

const DenialMessageText = styled.div`
  padding: 1rem;
  background-color: ${Theme.colors.lightBgGrey};
  color: ${Theme.colors.primary};
  line-height: 1.5;
  overflow-wrap: anywhere;

  p {
    margin-bottom: 0;
    white-space: pre-wrap;
  }

  p + p {
    margin-top: 0.75rem;
  }
`

const PhoneLink = styled.a`
  white-space: nowrap;
`

const KeyEventBoundary = styled.span`
  display: contents;
`

// Key events from the portaled dialog bubble through the React tree, so an
// enclosing Reactstrap modal (the Terminated modal) would otherwise run its own
// Tab trap and pull focus out of this dialog.
const stopPropagation = event => event.stopPropagation()

export const ContactLinks = () => (
  <>
    <a href={`mailto:${MEMBERSHIP_APPS_EMAIL}`}>{MEMBERSHIP_APPS_EMAIL}</a> or{" "}
    <PhoneLink href="tel:8004742776">{MEMBERSHIP_SERVICES_PHONE}</PhoneLink>
  </>
)

const ApplicationDenialModal = ({ isOpen, onClose, application }) => {
  const titleId = `application-denial-title-${application.id}`
  const typeLabel = applicationTypeLabel(application.applicationType)
  const reasonLabel = deniedReasonLabel(application.deniedReason)
  const paragraphs = (application.denialMessage ?? "")
    .split(/\r\n|\r|\n/)
    .map(line => line.trim())
    .filter(Boolean)
  const hasMessage = paragraphs.length > 0

  return (
    <StyledModal isOpen={isOpen} toggle={onClose} centered labelledBy={titleId}>
      <div className="modal-body">
        <ModalTitle id={titleId}>Application denied</ModalTitle>
        <DetailList>
          {typeLabel ? (
            <>
              <dt>Application</dt>
              <dd>{typeLabel}</dd>
            </>
          ) : null}
          {reasonLabel ? (
            <>
              <dt>Reason</dt>
              <dd>{reasonLabel}</dd>
            </>
          ) : null}
          {hasMessage ? (
            <>
              <dt>Message from Membership Services</dt>
              <dd>
                <DenialMessageText data-testid="application-denial-message">
                  {paragraphs.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </DenialMessageText>
              </dd>
            </>
          ) : null}
        </DetailList>
        <SubText>
          {hasMessage
            ? "Questions? Contact Membership Services at "
            : "No message was included. To learn more, contact Membership Services at "}
          <ContactLinks />.
        </SubText>
        <Button onClick={onClose}>Close</Button>
      </div>
    </StyledModal>
  )
}

const ApplicationDenialMessage = ({ applicationId, applicationType }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { data, error } = useQuery(MEMBERSHIP_APPLICATION_DENIAL_QUERY, {
    variables: { id: applicationId },
    context: { api: COACH_TOOLS_API },
    errorPolicy: "all",
  })
  const application = data?.membershipApplication

  if (error || !isDeniedPtmApplication(application?.status)) return null

  const open = event => {
    // The modal returns focus to the element focused when it opens, and
    // Safari does not focus a button on click.
    event.currentTarget.focus()
    setIsOpen(true)
  }

  const typeLabel = applicationTypeLabel(
    application.applicationType ?? applicationType,
  )

  return (
    <>
      <ViewMessageButton type="button" aria-haspopup="dialog" onClick={open}>
        View denial message
        {typeLabel ? (
          <span className="sr-only"> for {typeLabel} application</span>
        ) : null}
      </ViewMessageButton>
      <KeyEventBoundary onKeyDown={stopPropagation} onKeyUp={stopPropagation}>
        <ApplicationDenialModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          application={application}
        />
      </KeyEventBoundary>
    </>
  )
}

export default ApplicationDenialMessage
